import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from '@/lib/cn.js';

type Variant =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'outline'
  | 'danger';
type Size = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
}

const variants: Record<Variant, string> = {
  default: 'bg-ink-100 text-ink-900 hover:bg-white',
  primary: 'bg-beacon-400 text-ink-900 hover:bg-beacon-300',
  secondary:
    'bg-ink-700 text-ink-100 hover:bg-ink-600 border border-ink-600',
  ghost: 'text-ink-200 hover:bg-ink-700',
  outline: 'border border-ink-600 text-ink-100 hover:bg-ink-700',
  danger:
    'bg-danger-500/15 text-danger-400 border border-danger-500/30 hover:bg-danger-500/25',
};

const sizes: Record<Size, string> = {
  sm: 'h-7 px-2.5 text-[12px]',
  md: 'h-8 px-3 text-[13px]',
  lg: 'h-10 px-4 text-sm',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'default', size = 'md', className, ...rest }, ref) => (
    <button
      ref={ref}
      className={cn(
        'inline-flex items-center gap-1.5 font-medium rounded-md transition-colors focus-ring whitespace-nowrap disabled:opacity-50 disabled:pointer-events-none',
        variants[variant],
        sizes[size],
        className
      )}
      {...rest}
    />
  )
);
Button.displayName = 'Button';
